// Rules behind Setări, kept apart from the screens so they can be checked on their own.

export const ROLE_LABELS = { worker: 'Muncitor', accountant: 'Contabil', manager: 'Manager' };
export const ROLES = ['worker', 'accountant', 'manager'];
export const TYPE_LABELS = { parcels: 'Manager Parcele', invoice: 'Ajustare Stoc', arenda: 'Manager Arendă' };

export const emptySettings = () => ({ companies: [], locations: [], defaultCompany: '', defaultLocation: '', companyDetails: {} });

export const normalizeSettings = (saved) => ({ ...emptySettings(), ...(saved || {}), companyDetails: saved?.companyDetails || {} });

export const addCompany = (settings, name) => {
  const companies = [...settings.companies, name];
  return { ...settings, companies, defaultCompany: settings.defaultCompany || companies[0] };
};
export const removeCompany = (settings, name) => {
  const companies = settings.companies.filter((item) => item !== name);
  return { ...settings, companies, defaultCompany: settings.defaultCompany === name ? companies[0] || '' : settings.defaultCompany };
};
export const addLocation = (settings, name) => {
  const locations = [...settings.locations, name];
  return { ...settings, locations, defaultLocation: settings.defaultLocation || locations[0] };
};
export const removeLocation = (settings, name) => {
  const locations = settings.locations.filter((item) => item !== name);
  return { ...settings, locations, defaultLocation: settings.defaultLocation === name ? locations[0] || '' : settings.defaultLocation };
};
export const setCompanyDetails = (settings, name, details) => ({
  ...settings, companyDetails: { ...settings.companyDetails, [name]: details },
});

// ---------- Cadastral data ----------

const METADATA_KEYS = ['dataType', 'synced', 'farmId', 'lastModified'];

export const cadastralEntries = (files) => Object.entries(files || {})
  .filter(([name, data]) => data && typeof data === 'object' && !METADATA_KEYS.includes(name) && data.tNumbers)
  .map(([name, data]) => ({ name, ...data }));

export const cadastralRecordTotal = (files) => cadastralEntries(files).reduce((sum, entry) => sum + (entry.recordCount || 0), 0);

export const splitTNumbers = (text) => text.split(',').map((item) => item.trim()).filter(Boolean);

// Reads what is copied from the cadastre Excel: tab separated, first line is the header.
export const parseCadastralData = (text, tNumbers) => {
  const lines = text.split('\n').filter((line) => line.trim());
  const owners = {};

  for (let i = 1; i < lines.length; i++) {
    const columns = lines[i].split('\t');
    if (columns.length >= 3) {
      const ownerName = columns[0]?.trim();
      let cadastralNumber = '';
      let surfaceStr = '';
      let address = '';

      // The cadastral identifier is a short number, usually the second column, but it can shift.
      for (let j = 1; j < Math.min(columns.length, 4); j++) {
        const col = columns[j]?.trim();
        if (col && /^\d+$/.test(col) && col.length <= 4) {
          cadastralNumber = col;
          break;
        }
      }
      // The surface in square metres is the last number, over 100.
      for (let j = columns.length - 1; j >= 0; j--) {
        const col = columns[j]?.trim();
        if (col && /^\d+$/.test(col) && parseInt(col, 10) > 100) {
          surfaceStr = col;
          break;
        }
      }
      if (columns.length >= 6) address = columns[5]?.trim() || '';

      const surface = (parseInt(surfaceStr, 10) || 0) / 10000;
      if (surface >= 0.001 && surface <= 1000 && ownerName && cadastralNumber) {
        owners[ownerName] = { cadastralNumber, surface, address, tNumbers };
      }
    }
  }
  return owners;
};

export const cadastralFile = (owners, tNumbers, comuna, now = new Date().toISOString()) => ({
  tNumbers, owners, uploadDate: now, recordCount: Object.keys(owners).length, comuna,
});

// Old entries were saved before they had a comună; they all belong to Mircești.
export const migrateCadastral = (files) => {
  const entries = Object.values(files || {});
  if (!entries.some((file) => file && file.tNumbers && !file.comuna)) return null;
  const updated = {};
  Object.entries(files).forEach(([name, data]) => {
    updated[name] = data && data.tNumbers && !data.comuna ? { ...data, comuna: 'Mircești' } : data;
  });
  return updated;
};

// ---------- Recycle bin ----------

const parcelKey = (parcel) => (parcel.id !== undefined && parcel.id !== null ? `id:${parcel.id}` : `${parcel.cnp}|${parcel.t}|${parcel.locatie}|${parcel.parcela}`);

// Only a parcel that is really still there is skipped, not every parcel of the same person.
export const parcelsToRestore = (current, restored) => {
  const existing = new Set(current.map(parcelKey));
  return restored.filter((parcel) => !existing.has(parcelKey(parcel)));
};

export const invoicesToRestore = (current, restored) => {
  const existing = new Set(current.map((invoice) => invoice.id).filter(Boolean));
  return restored.filter((invoice) => !existing.has(invoice.id));
};

// A person gets one distribution a year, so one that was given again stays in the bin.
export const arendaToRestore = (current, restored) => restored.filter((record) => !current.some((item) => item.cnp === record.cnp && Number(item.year) === Number(record.year)));

// How many saved pages on this device still wait to reach the cloud.
export const countUnsynced = (storage, farmId) => {
  let count = 0;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && key.endsWith(`_${farmId}`) && String(storage.getItem(key)).includes('"synced":false')) count += 1;
  }
  return count;
};

// ---------- People ----------

export const emptyUser = () => ({ name: '', email: '', role: 'worker', companies: [], pages: [] });

export const userError = (user, existingEmails) => {
  const email = user.email.trim();
  if (!user.name.trim()) return 'Completează numele.';
  if (!/^\S+@\S+\.\S+$/.test(email)) return 'Completează un email corect.';
  if (existingEmails.includes(email)) return 'Există deja un cont cu acest email.';
  return '';
};

export const companiesText = (user) => {
  if (user.role === 'manager' || user.permissions?.companies?.includes('all')) return 'Toate firmele';
  const list = user.permissions?.companies || [];
  return list.length ? list.join(', ') : 'Nicio firmă';
};

export const toggled = (list, item) => (list.includes(item) ? list.filter((value) => value !== item) : [...list, item]);
